/* tslint:disable */
/* eslint-disable */
/**
 * Location check service API
 * Service to centralise business area handling for multiple depending services.
 *
*/


import * as runtime from '../runtime';
import type {
  AirportZoneListRto,
  LcRequestRto,
  LocationCheckResult,
} from '../models';
import {
    AirportZoneListRtoFromJSON,
    AirportZoneListRtoToJSON,
    LcRequestRtoFromJSON,
    LcRequestRtoToJSON,
    LocationCheckResultFromJSON,
    LocationCheckResultToJSON,
} from '../models';

export interface CheckPositionIsInBusinessAreaRequest {
    lcRequestRto: LcRequestRto;
}

export interface GetAirportZoneListRequest {
    locationId: number;
}

export interface GetAirportZonesRequest {
    iataAirportCode: string;
}

/**
 * ExternalApi - interface
 * 
 * @export
 * @interface ExternalApiInterface
 */
export interface ExternalApiInterface {
    /**
     *  * public REST interface to GET the zone that matches the provided position.   * This endpoint provides a rto containing check result (true|false), the zone and possible error reason.
     * @summary Endpoint to validate if a position is within the car2go business area.
     * @param {LcRequestRto} lcRequestRto 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ExternalApiInterface
     */
    checkPositionIsInBusinessAreaRaw(requestParameters: CheckPositionIsInBusinessAreaRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<LocationCheckResult>>;

    /**
     *  * public REST interface to GET the zone that matches the provided position.   * This endpoint provides a rto containing check result (true|false), the zone and possible error reason.
     * Endpoint to validate if a position is within the car2go business area.
     */
    checkPositionIsInBusinessArea(requestParameters: CheckPositionIsInBusinessAreaRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<LocationCheckResult>;

    /**
     *  * public REST interface to GET a list of airport zones by location id.
     * @summary Endpoint for getting airport zone list by location id
     * @param {number} locationId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ExternalApiInterface
     */
    getAirportZoneListRaw(requestParameters: GetAirportZoneListRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<AirportZoneListRto>>;

    /**
     *  * public REST interface to GET a list of airport zones by location id.
     * Endpoint for getting airport zone list by location id
     */
    getAirportZoneList(requestParameters: GetAirportZoneListRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<AirportZoneListRto>;

    /**
     * 
     * @summary Endpoint for getting airport zones by iataAirportCode
     * @param {string} iataAirportCode 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof ExternalApiInterface
     */
    getAirportZonesRaw(requestParameters: GetAirportZonesRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<AirportZoneListRto>>;

    /**
     * Endpoint for getting airport zones by iataAirportCode
     */
    getAirportZones(requestParameters: GetAirportZonesRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<AirportZoneListRto>;

}

/**
 * 
 */
export class ExternalApi extends runtime.BaseAPI implements ExternalApiInterface {

    /**
     *  * public REST interface to GET the zone that matches the provided position.   * This endpoint provides a rto containing check result (true|false), the zone and possible error reason.
     * Endpoint to validate if a position is within the car2go business area.
     */
    async checkPositionIsInBusinessAreaRaw(requestParameters: CheckPositionIsInBusinessAreaRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<LocationCheckResult>> {
        if (requestParameters.lcRequestRto === null || requestParameters.lcRequestRto === undefined) {
            throw new runtime.RequiredError('lcRequestRto','Required parameter requestParameters.lcRequestRto was null or undefined when calling checkPositionIsInBusinessArea.');
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        const response = await this.request({
            path: `/external/checkPositionIsInBusinessArea`,
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: LcRequestRtoToJSON(requestParameters.lcRequestRto),
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => LocationCheckResultFromJSON(jsonValue));
    }

    /**
     *  * public REST interface to GET the zone that matches the provided position.   * This endpoint provides a rto containing check result (true|false), the zone and possible error reason.
     * Endpoint to validate if a position is within the car2go business area.
     */
    async checkPositionIsInBusinessArea(requestParameters: CheckPositionIsInBusinessAreaRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<LocationCheckResult> {
        const response = await this.checkPositionIsInBusinessAreaRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     *  * public REST interface to GET a list of airport zones by location id.
     * Endpoint for getting airport zone list by location id
     */
    async getAirportZoneListRaw(requestParameters: GetAirportZoneListRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<AirportZoneListRto>> {
        if (requestParameters.locationId === null || requestParameters.locationId === undefined) {
            throw new runtime.RequiredError('locationId','Required parameter requestParameters.locationId was null or undefined when calling getAirportZoneList.');
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/external/getAirportZoneList/{locationId}`.replace(`{${"locationId"}}`, encodeURIComponent(String(requestParameters.locationId))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => AirportZoneListRtoFromJSON(jsonValue));
    }

    /**
     *  * public REST interface to GET a list of airport zones by location id.
     * Endpoint for getting airport zone list by location id
     */
    async getAirportZoneList(requestParameters: GetAirportZoneListRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<AirportZoneListRto> {
        const response = await this.getAirportZoneListRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Endpoint for getting airport zones by iataAirportCode
     */
    async getAirportZonesRaw(requestParameters: GetAirportZonesRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<AirportZoneListRto>> {
        if (requestParameters.iataAirportCode === null || requestParameters.iataAirportCode === undefined) {
            throw new runtime.RequiredError('iataAirportCode','Required parameter requestParameters.iataAirportCode was null or undefined when calling getAirportZones.');
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/external/getAirportZones/{iataAirportCode}`.replace(`{${"iataAirportCode"}}`, encodeURIComponent(String(requestParameters.iataAirportCode))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => AirportZoneListRtoFromJSON(jsonValue));
    }

    /**
     * Endpoint for getting airport zones by iataAirportCode
     */
    async getAirportZones(requestParameters: GetAirportZonesRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<AirportZoneListRto> {
        const response = await this.getAirportZonesRaw(requestParameters, initOverrides);
        return await response.value();
    }

}
